import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'

interface Option {
  id: string
  label: string
}

interface Props {
  options: Option[]
  selected: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  /** Search box placeholder + empty-state noun, so section pickers don't say "fields". */
  searchPlaceholder?: string
  emptyLabel?: string
}

/**
 * Compact multi-select rendered as an in-flow disclosure (not a floating
 * overlay) so it never clips inside the scrollable Properties sidebar. Collapsed
 * by default — the trigger summarizes the selection; expanding reveals a
 * searchable, scrollable checkbox list that stays bounded no matter how many
 * (or how long) the options are.
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'None',
  searchPlaceholder = 'Search fields…',
  emptyLabel = 'No fields match.',
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedSet = new Set(selected)
  const toggle = (id: string) =>
    onChange(selectedSet.has(id) ? selected.filter(s => s !== id) : [...selected, id])

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find(o => o.id === selected[0])?.label || '1 selected'
        : `${selected.length} selected`

  const q = query.trim().toLowerCase()
  const visible = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options

  return (
    <div className='rounded-md border'>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className={cn(
          'flex h-8 w-full items-center justify-between gap-2 px-3 text-sm',
          selected.length === 0 && 'text-muted-foreground',
        )}
      >
        <span className='truncate'>{summary}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 opacity-60 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className='border-t'>
          {options.length > 6 && (
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className='w-full border-b bg-transparent px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground'
            />
          )}
          <div className='max-h-44 overflow-y-auto p-1'>
            {visible.length === 0 ? (
              <p className='px-2 py-3 text-center text-xs text-muted-foreground'>{emptyLabel}</p>
            ) : (
              visible.map(o => (
                <label
                  key={o.id}
                  className='flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-accent'
                >
                  <Checkbox
                    checked={selectedSet.has(o.id)}
                    onCheckedChange={() => toggle(o.id)}
                  />
                  <span className='truncate'>{o.label || 'Untitled'}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
